(() => {
  if (window.PreaProductGridLoadMore) {
    window.PreaProductGridLoadMore.init(document);
    return;
  }

  const GRID_SELECTOR = '[data-prea-product-grid]';

  const initGrid = (grid) => {
    if (!grid || grid.dataset.preaLoadMoreReady === 'true') return;

    const section = grid.closest('[data-prea-best-sellers-section], [data-prea-new-arrivals-section]');
    const button = section?.querySelector('[data-prea-load-more-button]');
    const control = button?.closest('[data-prea-load-more-control]');
    const status = section?.querySelector('[data-prea-load-more-status]');
    let items = Array.from(grid.children).filter((item) => item.classList.contains('prea-product-card-wrapper'));
    const mobileInitialCount = Math.max(1, Number(grid.dataset.initialCount) || 6);
    const desktopInitialCount = Math.max(1, Number(grid.dataset.initialCountDesktop) || mobileInitialCount);
    const desktopMediaQuery = window.matchMedia('(min-width: 990px)');
    const getInitialCount = () => desktopMediaQuery.matches ? desktopInitialCount : mobileInitialCount;
    const batchSize = Math.max(1, Number(grid.dataset.batchSize) || 6);
    const totalCount = Math.max(items.length, Number(grid.dataset.totalCount) || items.length);
    const sectionId = grid.dataset.sectionId;
    let nextUrl = button?.dataset.preaNextUrl || '';
    let loading = false;
    let hasLoadedMore = false;

    grid.dataset.preaLoadMoreReady = 'true';
    items.slice(0, getInitialCount()).forEach((item) => item.classList.add('is-load-more-visible'));

    if (!button) {
      if (control) control.hidden = true;
      return;
    }

    const visibleCount = () => items.filter((item) => item.classList.contains('is-load-more-visible')).length;
    const updateControl = () => {
      const count = visibleCount();
      const hasHiddenItems = items.some((item) => !item.classList.contains('is-load-more-visible'));
      const hasMore = hasHiddenItems || Boolean(nextUrl);
      button.setAttribute('aria-expanded', String(!hasMore));
      button.disabled = loading;
      button.removeAttribute('aria-busy');
      if (control) control.hidden = !hasMore;
      if (status) status.textContent = `Showing ${count} of ${totalCount} products.`;
    };

    const revealItems = (nextItems) => {
      if (!nextItems.length) return;

      hasLoadedMore = true;
      nextItems.forEach((item, index) => {
        item.style.setProperty('--prea-load-more-index', String(index));
        item.classList.add('is-load-more-visible', 'is-load-more-revealing');
        item.addEventListener('animationend', () => {
          item.classList.remove('is-load-more-revealing');
          item.style.removeProperty('--prea-load-more-index');
        }, { once: true });
      });
    };

    const sectionRequestUrl = (url) => {
      const requestUrl = new URL(url, window.location.origin);
      if (sectionId) requestUrl.searchParams.set('section_id', sectionId);
      return requestUrl.toString();
    };

    const fetchNextItems = async () => {
      if (!nextUrl || loading) return [];

      loading = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(sectionRequestUrl(nextUrl), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error(`Product batch request failed: ${response.status}`);

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const remoteGrid = documentFragment.getElementById(grid.id);
        if (!remoteGrid) throw new Error('Product batch response did not contain the expected grid.');

        const remainingCount = Math.max(totalCount - items.length, 0);
        const appendedItems = Array.from(remoteGrid.children)
          .filter((item) => item.classList.contains('prea-product-card-wrapper'))
          .slice(0, remainingCount);
        appendedItems.forEach((item) => grid.appendChild(item));
        items = items.concat(appendedItems);

        const remoteButton = documentFragment.querySelector(`[data-prea-load-more-button][aria-controls="${grid.id}"]`);
        nextUrl = remoteButton?.dataset.preaNextUrl || '';
        return appendedItems;
      } catch (error) {
        console.error(error);
        if (status) status.textContent = 'Unable to load more products. Please try again.';
        return [];
      } finally {
        loading = false;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    };

    button.addEventListener('click', async () => {
      if (loading) return;

      let nextItems = items
        .filter((item) => !item.classList.contains('is-load-more-visible'))
        .slice(0, batchSize);

      if (!nextItems.length && nextUrl) {
        nextItems = await fetchNextItems();
      }

      revealItems(nextItems);

      window.setTimeout(updateControl, 70 * Math.max(nextItems.length - 1, 0) + 680);
    });

    const syncInitialCount = () => {
      if (hasLoadedMore) return;

      const initialCount = getInitialCount();
      items.forEach((item, index) => {
        item.classList.toggle('is-load-more-visible', index < initialCount);
      });
      updateControl();
    };

    desktopMediaQuery.addEventListener?.('change', syncInitialCount);

    updateControl();
  };

  const initAll = (root = document) => {
    if (root.matches?.(GRID_SELECTOR)) initGrid(root);
    root.querySelectorAll?.(GRID_SELECTOR).forEach(initGrid);
  };

  window.PreaProductGridLoadMore = { init: initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
